import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config.js';
import { parseApiJson, toApiError } from '../lib/api-error.js';

export type TicketingCapabilities = Record<string, boolean>;

export type TicketingProviderSummary = {
  id: string;
  displayName: string;
  capabilities: TicketingCapabilities;
  capabilityLabels: Record<string, string>;
};

export type TicketLink = {
  provider: string;
  externalTicketId: string;
  externalIdentifier: string;
  externalUrl: string;
  createdAt: string;
};

export type TicketingProviderStatus = {
  connected: boolean;
};

export type CreatedTicket = {
  externalId: string;
  identifier: string;
  url: string;
  /** An existing Cofactor-filed ticket was linked instead of filing another. */
  linkedExisting?: boolean;
  /** A requested type or labels the tracker no longer has, left off the ticket. */
  dropped?: { type: string | null; labels: string[] };
};

export type TicketFilingSettings = {
  defaultType: string | null;
  defaultLabels: string[];
  autoFile: boolean;
  /** Provider-specific settings; only GitHub has any. */
  extensions?: { github: { trackerRepo: string | null; availableRepos: string[] } };
};

type TicketFilingSettingsUpdate = {
  defaultType: string | null;
  defaultLabels: string[];
  autoFile: boolean;
  extensions: { github: { trackerRepo: string | null } };
};

type TaxonomyEntry = { name: string; description: string | null };

export type TicketTaxonomy = {
  /** Null when the labels are unknown (several repos and none chosen). */
  labels: TaxonomyEntry[] | null;
  /** Null when the tracker has no issue types. */
  types: TaxonomyEntry[] | null;
  notes: string[];
  extensions?: { github: { repo: string | null; repos: string[]; closeReasons: string[] } };
};

type DuplicateTicket = {
  externalId: string;
  identifier: string;
  title: string;
  url: string;
  status: string;
  labels: string[];
};

export function useTicketingProviders() {
  return useQuery({
    queryKey: ['ticketing-providers'],
    queryFn: async (): Promise<{ ok: boolean; data?: TicketingProviderSummary[] }> => {
      const res = await fetch(`${API_URL}/integrations/ticketing/providers`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; data?: TicketingProviderSummary[] }>(
        res,
        'Failed to fetch ticketing providers'
      );
    },
  });
}

export function useTicketingProvider(providerId: string) {
  const query = useTicketingProviders();
  const provider = query.data?.data?.find((p) => p.id === providerId) ?? null;
  return { ...query, provider };
}

export function useTicketingProviderStatus(providerId: string, enabled = true) {
  return useQuery({
    queryKey: ['ticketing-provider-status', providerId],
    queryFn: async (): Promise<{ ok: boolean; data: TicketingProviderStatus }> => {
      const res = await fetch(`${API_URL}/integrations/ticketing/${providerId}/status`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch ticketing provider status');
      return res.json();
    },
    enabled: enabled && providerId.length > 0,
  });
}

export function useTicketLink(providerId: string, issueId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['ticket-link', providerId, issueId],
    queryFn: async (): Promise<{ ok: boolean; data: TicketLink | null }> => {
      const res = await fetch(
        `${API_URL}/integrations/ticketing/${providerId}/ticket-link/${issueId}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to fetch ticket link');
      return res.json();
    },
    enabled: enabled && !!issueId,
  });
}

/** Why automatic filing gave up on this issue; null when it has not failed. */
export function useAutoFileFailure(providerId: string, issueId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['auto-file-failure', providerId, issueId],
    queryFn: async (): Promise<{ ok: boolean; data: { reason: string } | null }> => {
      const res = await fetch(
        `${API_URL}/integrations/ticketing/${providerId}/auto-file/${issueId}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to fetch auto-file status');
      return res.json();
    },
    enabled,
    retry: false,
  });
}

export function useCreateTicket(providerId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      issueId: string;
      providerOptions?: Record<string, unknown>;
    }): Promise<{ ok: boolean; ticket: CreatedTicket }> => {
      const res = await fetch(`${API_URL}/integrations/ticketing/${providerId}/create-ticket`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to create ticket');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['ticket-link', providerId, variables.issueId] });
      qc.invalidateQueries({ queryKey: ['ticket-links', variables.issueId] });
      qc.invalidateQueries({ queryKey: ['auto-file-failure', providerId, variables.issueId] });
    },
  });
}

/** The org's filing defaults. Rejects when the provider's filing is off or not connected. */
export function useFilingSettings(
  providerId: string,
  { enabled, forEditing = false }: { enabled: boolean; forEditing?: boolean }
) {
  return useQuery({
    queryKey: ['ticketing-filing-settings', providerId, forEditing],
    queryFn: async (): Promise<{ ok: boolean; data: TicketFilingSettings }> => {
      const params = forEditing ? '?forEditing=true' : '';
      const res = await fetch(`${API_URL}/integrations/ticketing/${providerId}/settings${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch filing settings');
      return res.json();
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useUpdateFilingSettings(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      update: TicketFilingSettingsUpdate
    ): Promise<{ ok: boolean; data: TicketFilingSettingsUpdate }> => {
      const res = await fetch(`${API_URL}/integrations/ticketing/${providerId}/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(update),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save filing settings');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticketing-filing-settings', providerId] });
    },
  });
}

/** Labels and types for one repo (or the provider default when `repo` is null). */
export function useTicketTaxonomy(providerId: string, repo: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['ticketing-taxonomy', providerId, repo],
    queryFn: async (): Promise<{ ok: boolean; data: TicketTaxonomy }> => {
      const params = repo ? `?repo=${encodeURIComponent(repo)}` : '';
      const res = await fetch(`${API_URL}/integrations/ticketing/${providerId}/taxonomy${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch labels and types');
      return res.json();
    },
    enabled,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useTicketDuplicates(
  providerId: string,
  issueId: string,
  repo: string | null,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['ticketing-duplicates', providerId, issueId, repo],
    queryFn: async (): Promise<{ ok: boolean; data: DuplicateTicket[] }> => {
      const params = repo ? `?repo=${encodeURIComponent(repo)}` : '';
      const res = await fetch(
        `${API_URL}/integrations/ticketing/${providerId}/duplicates/${issueId}${params}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to check for duplicate tickets');
      return res.json();
    },
    enabled,
    retry: false,
  });
}

export function useLinkTicket(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      issueId: string;
      ticket: string;
    }): Promise<{ ok: boolean; ticket: CreatedTicket }> => {
      const res = await fetch(`${API_URL}/integrations/ticketing/${providerId}/link-ticket`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to link ticket');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['ticket-link', providerId, variables.issueId] });
      qc.invalidateQueries({ queryKey: ['ticket-links', variables.issueId] });
      qc.invalidateQueries({ queryKey: ['auto-file-failure', providerId, variables.issueId] });
    },
  });
}
